"use client";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { IconUpload } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import {
    Dropzone,
    DropzoneContent,
    DropzoneEmptyState,
} from "@/components/misc/dropzone";
import { cn } from "@/lib/utils";
import { IUserData, type IAccountAssets } from "@/types";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useStateManagement } from "@/hooks/useStateManagement";

interface Props {
    addAsset?: (asset: IAccountAssets) => void;
    viewMode?: "grid" | "list";
    account?: IUserData;
}

export default function AssetsUpload({
    addAsset,
    viewMode = "grid",
    account,
}: Props) {
    // TODO: Implement account-based file size limits
    // For now, allow 100MB for all users to support video uploads
    const maxSize =
        account?.account_type === "bpro" ||
        account?.account_type === "bplus" ||
        account?.account_type === "premium"
            ? 100 * 1024 * 1024
            : 2 * 1024 * 1024; // 100 MB for paid and 2 MB for free

    const [selectedImage, setSelectedImage] = useState<boolean | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null); // Not used currently, but kept if you plan to add it back
    const [filePreviews, setFilePreviews] = useState<any>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [open, setOpen] = useState(false);
    const [files, setFiles] = useState<File[] | undefined>();
    const [error, setError] = useState<string | null>(null);

    const handleDrop = (files: File[]) => {
        setFiles(files);
        setFilePreviews([]);
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                setFilePreviews((prev: any) => [
                    ...prev,
                    {
                        name: file.name,
                        preview: e.target?.result,
                        uploaded: false,
                        uploading: false,
                        type: file.type, // Add file type to track whether it's video or image
                    },
                ]);
            };
            reader.readAsDataURL(file);
        });
        setSelectedImage(true);
    };

    /**
     * Processes an image File through a canvas to strip EXIF data.
     * Returns a Promise that resolves with a new Blob, or rejects on error.
     */
    const processImageForUpload = (file: File): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            // Create an object URL from the File to use as image source
            const objectURL = URL.createObjectURL(file);

            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                if (!ctx) {
                    URL.revokeObjectURL(objectURL); // Clean up
                    return reject(
                        new Error("Could not get 2D canvas context.")
                    );
                }

                // Set canvas dimensions to image dimensions
                canvas.width = img.width;
                canvas.height = img.height;

                // Draw the image onto the canvas. This implicitly handles orientation
                // based on how the browser renders the image, and discards EXIF.
                ctx.drawImage(img, 0, 0);

                // Export the canvas content as a new Blob.
                // For JPEGs, quality can be adjusted (0.0 to 1.0).
                // Ensure the output type is suitable for your server (e.g., 'image/jpeg')
                canvas.toBlob(
                    (newBlob) => {
                        URL.revokeObjectURL(objectURL); // Clean up the object URL immediately
                        if (newBlob) {
                            resolve(newBlob);
                        } else {
                            reject(
                                new Error("Failed to create Blob from canvas.")
                            );
                        }
                    },
                    file.type === "image/jpeg" ? "image/jpeg" : file.type, // Maintain original type or default to JPEG for common cases
                    0.9 // JPEG quality (adjust as needed, 0.9 is good balance)
                );
            };

            img.onerror = (errorEvent) => {
                URL.revokeObjectURL(objectURL); // Clean up
                reject(new Error("Failed to load image for processing."));
            };

            img.src = objectURL;
        });
    };

    async function Upload() {
        if (files && files.length > 0) {
            setIsUploading(true);
            setError(null); // Clear previous errors

            for (const file of files) {
                // Check file size on client-side before processing
                if (file.size > maxSize) {
                    setError(
                        `File "${file.name}" is larger than ${maxSize / (1024 * 1024)}MB. Please upload a smaller file.`
                    );
                    toast.error(`File "${file.name}" is too large.`, {
                        id: toast.loading(`Uploading ${file.name}...`),
                    });
                    setFilePreviews((prevPreviews: any) =>
                        prevPreviews.map((preview: any) =>
                            preview.name === file.name
                                ? {
                                      ...preview,
                                      uploading: false,
                                      uploaded: false,
                                  }
                                : preview
                        )
                    );
                    continue; // Skip this file and proceed to the next
                }

                const file_type = file.type.split("/").pop() || "octet-stream"; // Get extension like 'jpeg', 'png', 'webm'
                const file_name = uuidv4() + "." + file_type; // Generate unique filename
                const newTitle = file.name.replace(/\.[^/.]+$/, ""); // Title without extension
                const isVideo = file.type.startsWith("video/");
                const toastId = toast.loading(
                    isVideo
                        ? `Uploading ${file.name}...`
                        : `Processing and uploading ${file.name}...`
                );

                setFilePreviews((prevPreviews: any) =>
                    prevPreviews.map((preview: any) =>
                        preview.name === file.name
                            ? { ...preview, uploading: true, uploaded: false }
                            : preview
                    )
                );

                try {
                    // Process images to strip EXIF data, but skip processing for videos
                    const processedBlob = isVideo
                        ? file
                        : await processImageForUpload(file);
                    console.log(
                        `${isVideo ? "Video" : "Processed"} Blob for ${file.name}: Type ${processedBlob.type}, Size ${processedBlob.size} bytes`
                    );

                    // Send the processed Blob to your API
                    const response = await UploadAsset(
                        processedBlob, // Pass the processed Blob
                        file_name,
                        newTitle,
                        processedBlob.type // Pass the determined content type
                    );
                    const body = await response.json();

                    if (!response.ok) {
                        // If the server responded with an error status (e.g., 400, 500)
                        const serverErrorMessage =
                            body.error?.details ||
                            body.error?.message ||
                            body.error ||
                            "Unknown server error.";
                        throw new Error(`Upload failed: ${serverErrorMessage}`);
                    }

                    if (body.data && typeof addAsset === "function") {
                        addAsset(body.data);
                    }

                    toast.success("Uploaded successfully", { id: toastId });

                    setFilePreviews((prevPreviews: any) =>
                        prevPreviews.map((preview: any) =>
                            preview.name === file.name
                                ? {
                                      ...preview,
                                      uploading: false,
                                      uploaded: true,
                                  }
                                : preview
                        )
                    );
                } catch (error: any) {
                    console.error("Upload failed for", file.name, ":", error);
                    setError(
                        error.message ||
                            "An unknown error occurred during upload."
                    );
                    toast.error(`Error: ${error.message || "Upload failed"}`, {
                        id: toastId,
                    });

                    setFilePreviews((prevPreviews: any) =>
                        prevPreviews.map((preview: any) =>
                            preview.name === file.name
                                ? {
                                      ...preview,
                                      uploading: false,
                                      uploaded: false,
                                  }
                                : preview
                        )
                    );
                }
            }

            setIsUploading(false);
            setOpen(false); // Close dialog after all uploads
            setFiles(undefined); // Clear files
        }
    }

    // Modified to accept a Blob and its content type
    async function UploadAsset(
        processedBlob: Blob,
        file_name: string,
        title: string,
        contentType: string
    ) {
        return fetch(
            process.env.NEXT_PUBLIC_API_SERVER_INTERNAL +
                "/api/admin/v2/asset/",
            {
                method: "PUT",
                headers: {
                    "Content-Type": contentType, // Use the actual processed Blob's type
                    title: title,
                    "file-path": file_name,
                },
                body: processedBlob, // Send the Blob directly (browser fetch supports Blob)
            }
        );
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <div
                    className={cn(
                        "cursor-pointer overflow-hidden rounded-lg shadow transition hover:bg-muted hover:shadow-lg",
                        viewMode === "list" && "flex items-center gap-4"
                    )}
                >
                    <IconUpload
                        className={cn(
                            "object-cover",
                            viewMode === "grid"
                                ? "h-56 w-full p-12"
                                : "h-20 w-20"
                        )}
                    />
                    <div
                        className={cn(
                            "flex items-center justify-between gap-2 px-2 pb-3 pt-1",
                            viewMode === "list" && "bg-transparent p-0"
                        )}
                    >
                        <Label variant="headingtoggle" className="text-xs">
                            Upload
                        </Label>
                    </div>
                </div>
            </DialogTrigger>
            <DialogContent className="overflow-auto sm:h-fit sm:max-h-[80dvh]">
                <DialogHeader>
                    <DialogTitle>Upload Assets</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center">
                    {error && (
                        <Alert className="text-copy mb-4 w-fit border-destructive bg-destructive/30">
                            <AlertTitle>A problem occurred</AlertTitle>
                            <AlertDescription className="max-w-prose">
                                {error}
                            </AlertDescription>
                        </Alert>
                    )}
                    <Dropzone
                        onDrop={handleDrop}
                        accept={{
                            "image/*": [
                                ".png",
                                ".jpg",
                                ".jpeg",
                                ".webp",
                                ".svg",
                                ".gif",
                            ],
                            "video/*": [".webm"],
                        }}
                        className="group size-96 overflow-hidden rounded-md border-2 border-dashed p-2"
                        maxSize={maxSize}
                        maxFiles={5}
                        src={files}
                        onError={(dropzoneError) => {
                            // Renamed to avoid conflict with `error` state
                            if (
                                dropzoneError.message.includes("File is larger")
                            ) {
                                setError(
                                    `File size is too large. Please upload a file smaller than ${maxSize / (1024 * 1024)}MB.`
                                );
                                toast.error(
                                    `File size is too large. Please upload a file smaller than ${maxSize / (1024 * 1024)}MB.`
                                );
                            } else if (
                                dropzoneError.message.includes("Too many files")
                            ) {
                                setError(
                                    "Too many files selected. You can only upload 5 files at a time."
                                );
                                toast.error(
                                    "Too many files selected. You can only upload 5 files at a time."
                                );
                            } else {
                                setError(
                                    "Something went wrong. Please try again. " +
                                        dropzoneError.message
                                );
                                toast.error(
                                    "Something went wrong. Please try again. " +
                                        dropzoneError.message
                                );
                            }
                        }}
                        onDropAccepted={() => setError(null)}
                    >
                        <DropzoneEmptyState>
                            <IconUpload className="mb-4 h-12 w-12 text-muted-foreground" />
                            <Label variant={"toggle"} className="text-lg">
                                Drag and drop
                            </Label>
                            <Label
                                variant={"subtoggle"}
                                className="text-wrap text-sm text-muted-foreground"
                            >
                                or click to browse
                            </Label>
                            <Label
                                variant={"subtoggle"}
                                className="text-wrap pt-4 text-sm text-muted-foreground"
                            >
                                max 5 files at a time, {maxSize / (1024 * 1024)}
                                MB each
                            </Label>
                        </DropzoneEmptyState>
                        <DropzoneContent>
                            <div
                                className={cn(
                                    "grid h-full auto-rows-fr gap-2 overflow-y-auto",
                                    // Bento-style responsive grid layout
                                    filePreviews.length === 1 && "grid-cols-1",
                                    filePreviews.length === 2 && "grid-cols-1",
                                    filePreviews.length === 3 &&
                                        "grid-cols-[2fr_1fr] grid-rows-2",
                                    filePreviews.length === 4 &&
                                        "grid-cols-2 grid-rows-2",
                                    filePreviews.length === 5 &&
                                        "grid-cols-[2fr_1fr] grid-rows-[1fr_1fr]"
                                )}
                            >
                                {filePreviews.map(
                                    (filePreview: any, index: number) => (
                                        <div
                                            className={cn(
                                                "relative overflow-hidden rounded-md border shadow-lg group-hover:border-transparent",
                                                // Special positioning for certain layouts
                                                filePreviews.length === 3 &&
                                                    index === 0 &&
                                                    "row-span-2",
                                                filePreviews.length === 5 &&
                                                    index === 0 &&
                                                    "row-span-2"
                                            )}
                                            key={index}
                                        >
                                            {filePreview.type?.startsWith(
                                                "video/"
                                            ) ? (
                                                <video
                                                    src={
                                                        filePreview.preview.toString() ||
                                                        "/placeholder.svg"
                                                    }
                                                    className="h-full w-full object-cover"
                                                    muted
                                                    loop
                                                    autoPlay
                                                    playsInline
                                                />
                                            ) : (
                                                <img
                                                    src={
                                                        filePreview.preview.toString() ||
                                                        "/placeholder.svg"
                                                    }
                                                    alt={filePreview.name || ""}
                                                    className="h-full w-full object-cover"
                                                />
                                            )}
                                            <div className="absolute inset-0 flex w-full items-center justify-center font-light text-muted-foreground transition-all group-hover:bg-background/50">
                                                <span className="absolute bottom-0 w-full truncate bg-background/90 text-left text-xs transition-all group-hover:bg-accent">
                                                    {filePreview.uploading
                                                        ? "Processing & Uploading..."
                                                        : filePreview.uploaded
                                                          ? "Uploaded"
                                                          : filePreview.name}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        </DropzoneContent>
                    </Dropzone>
                </div>
                <DialogFooter>
                    <Button
                        disabled={!selectedImage || isUploading}
                        type="submit"
                        onClick={Upload}
                    >
                        {isUploading ? "Uploading..." : "Upload"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
